import { useState, useEffect, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from '../../../AuthContext';
import { fetchData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import SearchableDropdown from '../../common/SearchableDropdown';
import { useSessionValidation } from '../../../utils/sessionValidation';
import { Refresh, GetApp, ArrowBack } from '@mui/icons-material';
import { IconButton, Tooltip, Alert, Snackbar } from '@mui/material';
import { exportStyledExcel } from '../../../utils/excelExport';

const PRIMARY     = '#00695c';
const PRIMARY_MID = '#00897b';
const PRIMARY_LT  = '#e0f2f1';

const fmtN = (n) => (parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtQ = (n) => (parseFloat(n) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmt2Date = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
};

const iconBtn = {
    border: '1px solid rgba(255,255,255,0.3)', borderRadius: '8px',
    height: 29, width: 29, color: '#fff', flexShrink: 0,
    '&:hover': { background: 'rgba(255,255,255,0.1)' },
    '&.Mui-disabled': { color: 'rgba(255,255,255,0.3)' },
};

const MOVEMENT_COLORS = {
    'FAST MOVING':   { bg: '#dcfce7', color: '#059669', border: '#059669' },
    'MODERATE MOVING': { bg: '#fef9c3', color: '#ca8a04', border: '#ca8a04' },
    'SLOW MOVING':   { bg: '#ffedd5', color: '#ea580c', border: '#ea580c' },
    'DEAD STOCK':    { bg: '#fee2e2', color: '#dc2626', border: '#dc2626' },
    'DEAD':          { bg: '#fee2e2', color: '#dc2626', border: '#dc2626' },
};

const MovementBadge = ({ type }) => {
    const t = (type || '').toUpperCase();
    const style = MOVEMENT_COLORS[t] || { bg: '#f1f5f9', color: '#64748b', border: '#64748b' };
    return (
        <span style={{ background: style.bg, color: style.color, border: `1px solid ${style.border}`, padding: '2px 10px', borderRadius: 10, fontSize: 10, fontWeight: 700 }}>
            {t || '-'}
        </span>
    );
};

const FastSlowMovingItems = () => {
    useSessionValidation();
    const { companyId, branchId, token, companyName, username } = useAuth();
    const history = useHistory();

    const today = new Date();
    const [fromDate, setFromDate]       = useState(fmt2Date(new Date(today.getFullYear(), today.getMonth(), 1)));
    const [toDate, setToDate]           = useState(fmt2Date(today));
    const [categories, setCategories]   = useState([]);
    const [categoryId, setCategoryId]   = useState('0');
    const [movementFilter, setMovementFilter] = useState('ALL');
    const [data, setData]               = useState([]);
    const [loading, setLoading]         = useState(false);
    const [error, setError]             = useState('');
    const [fetched, setFetched]         = useState(false);
    const [search, setSearch]           = useState('');

    useEffect(() => {
        if (!companyId || !token) return;
        fetchData('CategoryApi', `/${companyId}`, token)
            .then(res => setCategories(res?.response_data || res || []))
            .catch(() => setCategories([]));
    }, [companyId, token]);

    useEffect(() => {
        if (companyId && branchId && token && fromDate && toDate) {
            if (new Date(fromDate) > new Date(toDate)) {
                setData([]);
                setFetched(false);
                setError('From Date cannot be greater than To Date');
                return;
            }
            fetchReport();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [companyId, branchId, token, categoryId]);

    const fetchReport = async () => {
        if (!fromDate || !toDate) { setError('Please select date range'); return; }
        if (new Date(fromDate) > new Date(toDate)) { setError('From Date cannot be greater than To Date'); return; }
        setLoading(true);
        try {
            const res = await fetchData(
                'InventoryReportApi',
                `/FastSlowMovingItems/${fromDate}/${toDate}/${companyId}/${branchId}/${categoryId || 0}`,
                token
            );
            const rows = res?.response_data || res || [];
            setData(Array.isArray(rows) ? rows : []);
            setFetched(true);
        } catch (err) {
            console.error(err);
            setError('Failed to fetch fast/slow moving items');
        } finally {
            setLoading(false);
        }
    };

    const filtered = useMemo(() => {
        let rows = data;
        if (movementFilter !== 'ALL') {
            rows = rows.filter(r => {
                const t = (r.MovementType || '').toUpperCase();
                // 'DEAD' filter should match both 'DEAD' and 'DEAD STOCK'
                if (movementFilter === 'DEAD') return t === 'DEAD' || t === 'DEAD STOCK';
                return t === movementFilter;
            });
        }
        if (search.trim()) {
            const q = search.toLowerCase();
            rows = rows.filter(r =>
                (r.Name || '').toLowerCase().includes(q) ||
                (r.Code || '').toLowerCase().includes(q) ||
                (r.CategoryName || '').toLowerCase().includes(q)
            );
        }
        return rows;
    }, [data, search, movementFilter]);

    const totals = useMemo(() => filtered.reduce((acc, r) => ({
        SaleQty:    acc.SaleQty    + (parseFloat(r.SaleQty)    || 0),
        ClosingQty: acc.ClosingQty + (parseFloat(r.ClosingQty) || 0),
    }), { SaleQty: 0, ClosingQty: 0 }), [filtered]);

    // movement type counts
    const counts = useMemo(() => data.reduce((acc, r) => {
        const raw = (r.MovementType || 'UNKNOWN').toUpperCase();
        // Normalize 'DEAD STOCK' → 'DEAD' for counting
        const t = raw === 'DEAD STOCK' ? 'DEAD' : raw;
        acc[t] = (acc[t] || 0) + 1;
        return acc;
    }, {}), [data]);

    const handleExport = async () => {
        if (!filtered.length) return;
        const headers = ['#', 'Code', 'Item Name', 'Category', 'Sale Qty', 'Closing Qty', 'Avg Daily Sale', 'Stock Days', 'Movement'];
        const rows = filtered.map((r, i) => [
            i + 1, r.Code, r.Name, r.CategoryName,
            r.SaleQty, r.ClosingQty, r.AvgDailySale, r.StockDays ?? '-', r.MovementType,
        ]);

        await exportStyledExcel({
            reportTitle: 'STOCK FAST / SLOW MOVING ITEMS REPORT',
            sheetName: 'Fast Slow Moving',
            fileName: `FastSlowMovingItems_${fromDate}_${toDate}.xlsx`,
            companyName,
            username,
            fromDate,
            toDate,
            headers: headers,
            rows,
            totalsRow: ['', '', '', 'Totals', fmtQ(totals.SaleQty), fmtQ(totals.ClosingQty), '', '', ''],
            columnWidths: [4, 10, 28, 18, 12, 12, 14, 12, 10],
        });
    };

    const thBase = { padding: '6px 8px', fontWeight: 700, fontSize: 11, whiteSpace: 'nowrap', border: '1px solid #4db6ac', color: '#fff', background: PRIMARY_MID };
    const tdBase = { padding: '5px 8px', fontSize: 11, whiteSpace: 'nowrap', border: '1px solid #e2e8f0' };

    const movementOptions = ['ALL', 'FAST MOVING', 'MODERATE MOVING', 'SLOW MOVING', 'DEAD'];

    return (
        <div style={{ minHeight: '100%', background: '#f0f2f5', padding: 12, fontFamily: "'Inter', sans-serif", boxSizing: 'border-box' }}>
            <LogoLoader open={loading} message="Loading fast/slow moving items..." />
            <Snackbar open={!!error} autoHideDuration={5000} onClose={() => setError('')} anchorOrigin={{ vertical: 'top', horizontal: 'center' }}>
                <Alert severity="error" onClose={() => setError('')} sx={{ width: '100%' }}>{error}</Alert>
            </Snackbar>

            <div style={{ background: '#fff', borderRadius: 10, boxShadow: '0 4px 12px rgba(0,0,0,0.08)', border: '1px solid #e2e8f0', overflow: 'hidden' }}>

                {/* Toolbar */}
                <div className="report-toolbar" style={{ display: 'flex', alignItems: 'center', padding: '6px 12px', background: `linear-gradient(135deg, ${PRIMARY} 0%, ${PRIMARY_MID} 100%)`, gap: 6, flexWrap: 'nowrap', overflowX: 'auto', overflowY: 'hidden', scrollbarWidth: 'none' }}>
                    <Tooltip title="Back" arrow>
                        <IconButton onClick={() => history.push('/ReportsHub')} size="small" sx={iconBtn}>
                            <ArrowBack sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <span style={{ fontSize: 13, fontWeight: 700, color: '#fff', whiteSpace: 'nowrap', flexShrink: 0 }}>
                        Fast / Slow Moving Items
                        <span style={{ fontSize: 11, fontWeight: 400, opacity: 0.8, display: 'block', lineHeight: 1.2 }}>Inventory Report</span>
                    </span>
                    <div style={{ flex: 1, minWidth: 4 }} />
                    {/* Movement type filter */}
                    <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                    {movementOptions.map(opt => {
                            const mc = MOVEMENT_COLORS[opt] || { bg: 'rgba(255,255,255,0.2)', color: '#fff', border: 'rgba(255,255,255,0.4)' };
                            const active = movementFilter === opt;
                            const label = opt === 'ALL' ? 'ALL' : opt === 'FAST MOVING' ? 'FAST' : opt === 'MODERATE MOVING' ? 'MODERATE' : opt === 'SLOW MOVING' ? 'SLOW' : 'DEAD';
                            return (
                                <button key={opt} onClick={() => setMovementFilter(opt)}
                                    style={{ height: 26, padding: '0 10px', fontSize: 10, fontWeight: 700, borderRadius: 6, cursor: 'pointer', border: `1px solid ${active ? '#fff' : 'rgba(255,255,255,0.3)'}`, background: active ? '#fff' : 'rgba(255,255,255,0.1)', color: active ? PRIMARY : '#fff', transition: 'all 0.15s' }}>
                                    {label}{opt !== 'ALL' && counts[opt] ? ` (${counts[opt]})` : ''}
                                </button>
                            );
                        })}
                    </div>
                    <SearchableDropdown
                        value={categoryId}
                        onChange={v => setCategoryId(v)}
                        options={[{ value: '0', label: 'All Categories' }, ...categories.map(c => ({ value: String(c.Id), label: c.Name }))]}
                        placeholder="All Categories"
                        minWidth={160}
                        primaryColor={PRIMARY}
                    />
                    <input placeholder="Search item / category..."
                        value={search} onChange={e => setSearch(e.target.value)}
                        style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 10px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0, minWidth: 150, outline: 'none' }} />
                    <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)}
                        style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
                    <input type="date" value={toDate} onChange={e => setToDate(e.target.value)}
                        style={{ height: 29, border: '1px solid rgba(255,255,255,0.3)', borderRadius: 8, padding: '0 8px', fontSize: 12, backgroundColor: 'rgba(255,255,255,0.1)', color: '#fff', colorScheme: 'dark', flexShrink: 0 }} />
                    <Tooltip title="Refresh" arrow>
                        <IconButton onClick={fetchReport} disabled={loading} size="small" sx={iconBtn}>
                            <Refresh sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Export Excel" arrow>
                        <IconButton onClick={handleExport} disabled={!filtered.length} size="small" sx={iconBtn}>
                            <GetApp sx={{ fontSize: 16 }} />
                        </IconButton>
                    </Tooltip>
                </div>

                {/* Summary bar */}
                {fetched && data.length > 0 && (
                    <div style={{ display: 'flex', gap: 8, padding: '6px 14px', borderBottom: '1px solid #f1f5f9', flexWrap: 'wrap' }}>
                        {[
                            { label: 'Total Items', value: data.length,                    color: PRIMARY },
                            { label: 'Fast',        value: counts['FAST MOVING']   || 0,   color: '#059669' },
                            { label: 'Moderate',    value: counts['MODERATE MOVING'] || 0,   color: '#ca8a04' },
                            { label: 'Slow',        value: counts['SLOW MOVING']   || 0,   color: '#ea580c' },
                            { label: 'Dead',        value: counts['DEAD']          || 0,   color: '#dc2626' },
                            { label: 'Sale Qty',    value: fmtQ(totals.SaleQty),            color: '#0277bd' },
                            { label: 'Closing Qty', value: fmtQ(totals.ClosingQty),         color: PRIMARY },
                        ].map(s => (
                            <div key={s.label} style={{ background: PRIMARY_LT, borderRadius: 8, padding: '4px 12px', display: 'flex', flexDirection: 'column', alignItems: 'center', minWidth: 80 }}>
                                <span style={{ fontSize: 10, color: '#64748b', fontWeight: 600 }}>{s.label}</span>
                                <span style={{ fontSize: 12, color: s.color, fontWeight: 700 }}>{s.value}</span>
                            </div>
                        ))}
                    </div>
                )}

                {/* Meta */}
                {fetched && (
                    <div style={{ padding: '4px 14px', fontSize: 11, color: '#64748b', borderBottom: '1px solid #f1f5f9' }}>
                        {fromDate} to {toDate} &nbsp;|&nbsp; {filtered.length} items
                        {(search || movementFilter !== 'ALL') && ` (filtered from ${data.length})`}
                    </div>
                )}

                {/* Table */}
                <div className="report-table-scroll" style={{ overflow: 'auto', overflowY: 'scroll', maxHeight: 'calc(100vh - 190px)' }}>
                    <table style={{ borderCollapse: 'collapse', fontSize: 11, minWidth: 850, width: '100%' }}>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 3 }}>
                            <tr>
                                <th style={{ ...thBase, textAlign: 'center', width: 36 }}>#</th>
                                <th style={{ ...thBase, textAlign: 'left', minWidth: 220 }}>Item</th>
                                <th style={{ ...thBase, textAlign: 'left' }}>Category</th>
                                <th style={{ ...thBase, textAlign: 'right' }}>Sale Qty</th>
                                <th style={{ ...thBase, textAlign: 'right' }}>Closing Qty</th>
                                <th style={{ ...thBase, textAlign: 'right' }}>Avg Daily Sale</th>
                                <th style={{ ...thBase, textAlign: 'right' }}>Stock Days</th>
                                <th style={{ ...thBase, textAlign: 'center', background: '#004d40' }}>Movement</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.length === 0 ? (
                                <tr>
                                    <td colSpan={8} style={{ textAlign: 'center', padding: 32, color: '#94a3b8', fontSize: 13 }}>
                                        {fetched ? 'No records found' : 'Select date range and click Refresh'}
                                    </td>
                                </tr>
                            ) : filtered.map((r, i) => (
                                <tr key={`${r.Id}-${i}`} style={{ background: i % 2 === 0 ? '#fff' : PRIMARY_LT }}>
                                    <td style={{ ...tdBase, textAlign: 'center', color: '#94a3b8' }}>{i + 1}</td>
                                    <td style={{ ...tdBase, fontWeight: 600, color: PRIMARY }}>
                                        <span style={{ color: '#64748b', fontSize: 10, marginRight: 4 }}>{r.Code}</span>{r.Name || '-'}
                                    </td>
                                    <td style={{ ...tdBase, color: '#64748b' }}>{r.CategoryName || '-'}</td>
                                    <td style={{ ...tdBase, textAlign: 'right', color: '#0277bd', fontWeight: 600 }}>{fmtQ(r.SaleQty)}</td>
                                    <td style={{ ...tdBase, textAlign: 'right', color: PRIMARY, fontWeight: 600 }}>{fmtQ(r.ClosingQty)}</td>
                                    <td style={{ ...tdBase, textAlign: 'right' }}>{fmtN(r.AvgDailySale)}</td>
                                    <td style={{ ...tdBase, textAlign: 'right', color: '#374151' }}>{r.StockDays != null ? fmtN(r.StockDays) : '-'}</td>
                                    <td style={{ ...tdBase, textAlign: 'center' }}><MovementBadge type={r.MovementType} /></td>
                                </tr>
                            ))}
                        </tbody>
                        {filtered.length > 0 && (
                            <tfoot>
                                <tr style={{ background: PRIMARY_LT, fontWeight: 700 }}>
                                    <td colSpan={3} style={{ ...tdBase, fontWeight: 700, color: PRIMARY, textAlign: 'right' }}>Totals</td>
                                    <td style={{ ...tdBase, textAlign: 'right', color: '#0277bd', fontWeight: 700 }}>{fmtQ(totals.SaleQty)}</td>
                                    <td style={{ ...tdBase, textAlign: 'right', color: PRIMARY, fontWeight: 700 }}>{fmtQ(totals.ClosingQty)}</td>
                                    <td colSpan={3} style={{ ...tdBase }} />
                                </tr>
                            </tfoot>
                        )}
                    </table>
                </div>
            </div>
        </div>
    );
};

export default FastSlowMovingItems;
